import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Image as ImageIcon, LayoutGrid, History, Package, Newspaper,
  LifeBuoy, MessageSquare, Users2, Settings, LogOut, Plus, Trash2, Edit3, X, Save, Mail, Upload, Link as LinkIcon, Download,
  KeyRound, ShieldCheck, Eye, EyeOff, TrendingUp, MapPin, ExternalLink, BarChart3, Activity,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useSiteContent } from '../context/SiteContentContext';
import { resolveImageUrl } from '../utils/urls';
import RichTextField from '../components/RichTextField';
import axios from 'axios';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, AreaChart, Area,
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ============ Schemas per collection (fields to edit) ============
const SCHEMAS = {
  'hero-slides': {
    label: 'Hero Slides',
    fields: [
      { key: 'badge', label: 'Badge (e.g. 300T)', type: 'text' },
      { key: 'edition', label: 'Edition', type: 'text' },
      { key: 'title', label: 'Title', type: 'text' },
      { key: 'title_size', label: 'Ukuran Title', type: 'select', options: [
        { value: '', label: 'Default (Besar)' },
        { value: 'sm', label: 'Kecil (xl/2xl/3xl)' },
        { value: 'md', label: 'Sedang (2xl/3xl/4xl)' },
        { value: 'lg', label: 'Besar (3xl/4xl/5xl) — Default' },
        { value: 'xl', label: 'Sangat Besar (4xl/5xl/6xl)' },
        { value: '2xl', label: 'Extra Besar (5xl/6xl/7xl)' },
      ] },
      { key: 'subtitle', label: 'Subtitle', type: 'text' },
      { key: 'subtitle_size', label: 'Ukuran Subtitle', type: 'select', options: [
        { value: '', label: 'Default (Sedang)' },
        { value: 'xs', label: 'Sangat Kecil (sm/base/lg)' },
        { value: 'sm', label: 'Kecil (base/lg/xl)' },
        { value: 'md', label: 'Sedang (lg/xl/2xl) — Default' },
        { value: 'lg', label: 'Besar (xl/2xl/3xl)' },
        { value: 'xl', label: 'Sangat Besar (2xl/3xl/4xl)' },
      ] },
      { key: 'features', label: 'Features (comma-separated)', type: 'csv' },
      { key: 'image', label: 'Gambar / Banner', type: 'image' },
      { key: 'link', label: 'Link URL', type: 'text' },
      { key: 'background_mode', label: 'Mode Background (Full-Cover) — gambar mengisi seluruh area hero', type: 'checkbox' },
      { key: 'show_text', label: 'Tampilkan Text & Tombol (aktifkan jika banner tidak punya tulisan sendiri)', type: 'checkbox' },
      { key: 'order', label: 'Order', type: 'number' },
    ],
  },
  'product-categories': {
    label: 'Beranda · Product Categories',
    fields: [
      { key: 'name', label: 'Name (EN)', type: 'text' },
      { key: 'nameId', label: 'Name (ID)', type: 'text' },
      { key: 'description', label: 'Description', type: 'textarea' },
      { key: 'image', label: 'Gambar', type: 'image' },
      { key: 'link', label: 'Link URL', type: 'text' },
      { key: 'order', label: 'Order', type: 'number' },
    ],
  },
  'product-subcategories': {
    label: 'Sub-Kategori Produk',
    fields: [
      { key: 'parent', label: 'Parent Category ID (fusion-splicing / instruments / odn / fiber-cables)', type: 'text' },
      { key: 'name', label: 'Name (EN)', type: 'text' },
      { key: 'nameId', label: 'Name (ID)', type: 'text' },
      { key: 'description', label: 'Description', type: 'textarea' },
      { key: 'image', label: 'Gambar', type: 'image' },
      { key: 'order', label: 'Order', type: 'number' },
    ],
  },
  'products': {
    label: 'Produk',
    fields: [
      { key: 'name', label: 'Name', type: 'text' },
      { key: 'model', label: 'Model (mis. 300T, KL-500)', type: 'text' },
      { key: 'category', label: 'Category ID (fusion-splicing / instruments / …)', type: 'text' },
      { key: 'subcategory', label: 'Subcategory ID (fusion-splicer / fiber-tools / …)', type: 'text' },
      { key: 'tagline', label: 'Tagline', type: 'text' },
      { key: 'description', label: 'Description', type: 'textarea' },
      { key: 'features', label: 'Features (comma-separated)', type: 'csv' },
      { key: 'image', label: 'Gambar Produk', type: 'image' },
      { key: 'link', label: 'Link URL', type: 'text' },
      { key: 'order', label: 'Order', type: 'number' },
      { key: 'seo_title', label: 'SEO · Meta Title (override)', type: 'text' },
      { key: 'seo_description', label: 'SEO · Meta Description (override)', type: 'textarea' },
      { key: 'seo_og_image', label: 'SEO · OG Image (override)', type: 'image' },
    ],
  },
  'news-items': {
    label: 'Berita',
    fields: [
      { key: 'title', label: 'Title', type: 'text' },
      { key: 'category', label: 'Category', type: 'text' },
      { key: 'date', label: 'Date (text)', type: 'text' },
      { key: 'image', label: 'Gambar Berita', type: 'image' },
      { key: 'link', label: 'Link URL', type: 'text' },
      { key: 'order', label: 'Order', type: 'number' },
    ],
  },
  'support-items': {
    label: 'Dukungan',
    fields: [
      { key: 'title', label: 'Title (EN)', type: 'text' },
      { key: 'titleId', label: 'Title (ID)', type: 'text' },
      { key: 'description', label: 'Description', type: 'textarea' },
      { key: 'icon', label: 'Icon (Shield/FileText/PlayCircle)', type: 'text' },
      { key: 'link', label: 'Link URL', type: 'text' },
      { key: 'order', label: 'Order', type: 'number' },
    ],
  },
  'support-manuals': {
    label: 'Manual Produk',
    fields: [
      { key: 'title', label: 'Judul', type: 'text' },
      { key: 'category', label: 'Kategori (User Manual / Installation Guide / Catalogue)', type: 'text' },
      { key: 'model', label: 'Model Produk', type: 'text' },
      { key: 'thumb', label: 'Thumbnail', type: 'image' },
      { key: 'url', label: 'File URL (PDF)', type: 'text' },
      { key: 'order', label: 'Order', type: 'number' },
    ],
  },
  'support-videos': {
    label: 'Video Tutorial',
    fields: [
      { key: 'title', label: 'Judul', type: 'text' },
      { key: 'model', label: 'Model Produk', type: 'text' },
      { key: 'thumb', label: 'Thumbnail', type: 'image' },
      { key: 'url', label: 'Video URL (MP4)', type: 'text' },
      { key: 'order', label: 'Order', type: 'number' },
    ],
  },
  'partners': {
    label: 'Mitra',
    fields: [
      { key: 'name', label: 'Name', type: 'text' },
      { key: 'website', label: 'Website URL', type: 'text' },
      { key: 'logo', label: 'Logo (opsional)', type: 'image' },
      { key: 'order', label: 'Order', type: 'number' },
    ],
  },
  'stats': {
    label: 'Sejarah · Statistik',
    fields: [
      { key: 'icon', label: 'Icon (Award/Users/Globe)', type: 'text' },
      { key: 'value', label: 'Value', type: 'text' },
      { key: 'suffix', label: 'Suffix', type: 'text' },
      { key: 'label', label: 'Label', type: 'text' },
      { key: 'description', label: 'Description', type: 'textarea' },
      { key: 'order', label: 'Order', type: 'number' },
    ],
  },
  'seo-pages': {
    label: 'SEO Per-Halaman',
    fields: [
      { key: 'path', label: 'Path (mis. /, /produk, /sejarah, /dukungan)', type: 'text' },
      { key: 'title', label: 'Meta Title (override)', type: 'text' },
      { key: 'description', label: 'Meta Description (override, max 160 karakter)', type: 'textarea' },
      { key: 'keywords', label: 'Keywords (comma-separated)', type: 'textarea' },
      { key: 'og_image', label: 'OG Image (override)', type: 'image' },
      { key: 'canonical', label: 'Canonical URL (opsional)', type: 'text' },
      { key: 'noindex', label: 'Noindex (jangan indeks halaman ini)', type: 'checkbox' },
      { key: 'order', label: 'Order', type: 'number' },
    ],
  },
};

const SINGLETON_SCHEMAS = {
  'about-content': {
    label: 'Sejarah · About Content',
    fields: [
      { key: 'title', label: 'Title', type: 'text' },
      { key: 'heading_id', label: 'Heading ID', type: 'text' },
      { key: 'heading_size', label: 'Ukuran Heading ID', type: 'select', options: [
        { value: '', label: 'Default (Sedang) — 2xl/3xl' },
        { value: 'xs', label: 'Sangat Kecil — sm/base' },
        { value: 'sm', label: 'Kecil — base/lg/xl' },
        { value: 'md', label: 'Sedang — lg/xl/2xl' },
        { value: 'lg', label: 'Besar — xl/2xl/3xl (Default)' },
        { value: 'xl', label: 'Sangat Besar — 2xl/3xl/4xl' },
      ] },
      { key: 'subheading', label: 'Subheading', type: 'textarea' },
      { key: 'subheading_size', label: 'Ukuran Subheading', type: 'select', options: [
        { value: '', label: 'Default (Besar) — 3xl/5xl' },
        { value: 'xs', label: 'Sangat Kecil — base/lg' },
        { value: 'sm', label: 'Kecil — lg/xl/2xl' },
        { value: 'md', label: 'Sedang — xl/2xl/3xl' },
        { value: 'lg', label: 'Besar — 2xl/3xl/4xl' },
        { value: 'xl', label: 'Sangat Besar — 3xl/4xl/5xl (Default)' },
        { value: '2xl', label: 'Extra Besar — 4xl/5xl/6xl' },
      ] },
      { key: 'paragraph1', label: 'Paragraph 1', type: 'richtext' },
      { key: 'paragraph2', label: 'Paragraph 2', type: 'richtext' },
      { key: 'video_url', label: 'Video URL (YouTube embed)', type: 'text' },
      { key: 'video_thumbnail', label: 'Thumbnail Video', type: 'image' },
      { key: 'badges', label: 'Badges (comma-separated)', type: 'csv' },
      { key: 'banner_image', label: 'Banner Image', type: 'image' },
    ],
  },
  'site-config': {
    label: 'Konfigurasi Situs',
    fields: [
      { key: 'phone', label: 'Phone', type: 'text' },
      { key: 'phone_label', label: 'Phone label', type: 'text' },
      { key: 'email', label: 'Email', type: 'text' },
      { key: 'address', label: 'Address', type: 'textarea' },
      { key: 'whatsapp', label: 'WhatsApp', type: 'text' },
      { key: 'whatsapp_widget_enabled', label: 'Aktifkan Widget WhatsApp (bubble di pojok kanan bawah)', type: 'checkbox' },
      { key: 'whatsapp_agent_name', label: 'Nama Agent WhatsApp (default: "Sales JILONG Indonesia")', type: 'text' },
      { key: 'whatsapp_status', label: 'Status Widget WhatsApp (default: "Biasanya membalas dalam 1 jam")', type: 'text' },
      { key: 'whatsapp_greeting', label: 'Pesan Sambutan WhatsApp (muncul saat pengunjung buka chat)', type: 'textarea' },
      { key: 'whatsapp_quick_replies', label: 'Balasan Cepat WhatsApp (pisahkan dengan koma)', type: 'csv' },

      { key: '__section_service_center', label: '━━━ Kartu Service Center (Halaman Kontak) ━━━', type: 'section' },
      { key: 'service_center_title', label: 'Judul Kartu (default: "Service Center Jakarta")', type: 'text' },
      { key: 'service_center_subtitle', label: 'Sub-judul / Deskripsi Singkat', type: 'textarea' },
      { key: 'service_center_hours_label', label: 'Label Jam Operasional (default: "Jam Operasional")', type: 'text' },
      { key: 'service_center_hours_weekday', label: 'Jam Weekday (default: "Senin – Jumat, 09.00 – 17.00 WIB")', type: 'text' },
      { key: 'service_center_hours_weekend', label: 'Jam Weekend (default: "Sabtu 09.00 – 13.00 WIB · Minggu Tutup")', type: 'text' },
      { key: 'service_center_address', label: 'Alamat Service Center (kosongkan → pakai Address di atas)', type: 'textarea' },
      { key: 'service_center_wa_button_label', label: 'Label Tombol WhatsApp (default: "Chat WhatsApp Sekarang")', type: 'text' },
      { key: 'service_center_wa_message', label: 'Pesan Awal WhatsApp saat diklik', type: 'textarea' },
      { key: 'service_center_phone_button_label', label: 'Label Tombol Telepon (default: "Telepon Service Center")', type: 'text' },

      { key: '__section_links', label: '━━━ Link Umum ━━━', type: 'section' },
      { key: 'online_store_url', label: 'Online Store URL', type: 'text' },
      { key: 'anniversary_url', label: 'Anniversary Badge URL', type: 'text' },
      { key: 'search_url', label: 'Search URL', type: 'text' },
      { key: 'facebook', label: 'Facebook', type: 'text' },
      { key: 'instagram', label: 'Instagram', type: 'text' },
      { key: 'youtube', label: 'YouTube', type: 'text' },
      { key: 'linkedin', label: 'LinkedIn', type: 'text' },
      { key: 'privacy_url', label: 'Privacy URL', type: 'text' },
      { key: 'terms_url', label: 'Terms URL', type: 'text' },
    ],
  },
  'seo-config': {
    label: 'SEO Umum',
    fields: [
      { key: 'site_name', label: 'Site Name (mis. JILONG Indonesia)', type: 'text' },
      { key: 'site_url', label: 'Site URL (untuk canonical, mis. https://jilong-branch-id.emergent.host)', type: 'text' },
      { key: 'title_template', label: 'Title Template ({page} akan diganti nama halaman)', type: 'text' },
      { key: 'default_title', label: 'Default Meta Title (fallback)', type: 'text' },
      { key: 'default_description', label: 'Default Meta Description (max 160 karakter)', type: 'textarea' },
      { key: 'default_keywords', label: 'Default Keywords (comma-separated)', type: 'textarea' },
      { key: 'default_og_image', label: 'Default OG Image (URL, min 1200×630)', type: 'image' },
      { key: 'twitter_handle', label: 'Twitter Handle (mis. @jilongid)', type: 'text' },
      { key: 'ga4_measurement_id', label: 'Google Analytics 4 Measurement ID (G-XXXXXXXXXX)', type: 'text' },
      { key: 'gsc_verification', label: 'Google Search Console verification token', type: 'text' },
      { key: 'robots_txt', label: 'Robots.txt content ({site_url} akan diganti otomatis)', type: 'textarea' },
    ],
  },
};

// ============ Reusable Form ============
const ImageField = ({ value, onChange, testid }) => {
  const { authAxios } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [urlText, setUrlText] = useState('');
  const inputRef = useRef(null);

  const upload = async (file) => {
    if (!file) return;
    if (!file.type?.startsWith('image/')) {
      setError('Hanya file gambar (jpg/png/webp/gif) yang diperbolehkan.');
      return;
    }
    setError(''); setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await authAxios().post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      onChange(r.data.url);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Upload gagal.');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const onDrop = (e) => {
    e.preventDefault(); e.stopPropagation();
    setDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) upload(file);
  };

  const applyUrl = () => {
    const trimmed = (urlText || '').trim();
    if (!trimmed) { setError('URL kosong.'); return; }
    if (!/^https?:\/\//i.test(trimmed) && !trimmed.startsWith('/')) {
      setError('URL harus diawali http://, https://, atau /'); return;
    }
    setError('');
    onChange(trimmed);
    setUrlText('');
    setShowUrlInput(false);
  };

  const onPasteEvent = async (e) => {
    // Support pasting image from clipboard directly (Ctrl+V an image copy)
    const items = e.clipboardData?.items || [];
    for (const it of items) {
      if (it.type?.startsWith('image/')) {
        const file = it.getAsFile();
        if (file) {
          e.preventDefault();
          upload(file);
          return;
        }
      }
    }
  };

  const preview = resolveImageUrl(value);

  return (
    <div className="space-y-2" onPaste={onPasteEvent}>
      {preview ? (
        <div
          className={`flex flex-col sm:flex-row items-start gap-4 p-3 border rounded-lg transition-colors ${
            dragOver ? 'border-[#0a3d8f] bg-blue-50 border-2 border-dashed' : 'border-gray-200 bg-gray-50'
          }`}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
        >
          <img
            src={preview} alt="preview"
            className="h-28 w-44 object-cover rounded-md border border-gray-200 bg-white shrink-0"
            data-testid={`${testid}-preview`}
          />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-gray-900 mb-1">Gambar terpilih</p>
            <p className="text-xs text-gray-500 truncate mb-3" title={value}>{value}</p>
            <div className="flex flex-wrap gap-2">
              <label
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold border border-[#0a3d8f] text-[#0a3d8f] hover:bg-[#0a3d8f] hover:text-white cursor-pointer transition-colors ${uploading ? 'opacity-60 pointer-events-none' : ''}`}
                data-testid={`${testid}-replace-btn`}
              >
                <Upload size={12} />
                {uploading ? 'Mengunggah...' : 'Ganti (Upload)'}
                <input
                  ref={inputRef} type="file" accept="image/*" className="hidden"
                  onChange={(e) => upload(e.target.files?.[0])}
                  data-testid={`${testid}-file`}
                />
              </label>
              <button
                type="button"
                onClick={() => setShowUrlInput((s) => !s)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold border border-gray-300 text-gray-700 hover:bg-gray-100 transition-colors"
                data-testid={`${testid}-url-btn`}
              >
                <LinkIcon size={12} /> {showUrlInput ? 'Batal URL' : 'Tempel URL'}
              </button>
              <button
                type="button" onClick={() => onChange('')}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold border border-red-200 text-red-600 hover:bg-red-50 transition-colors"
                data-testid={`${testid}-clear`}
              >
                <Trash2 size={12} /> Hapus
              </button>
            </div>
            <p className="text-[11px] text-gray-400 mt-2">Tips: seret &amp; lepas file di atas kotak ini, atau tempel gambar (Ctrl+V) langsung.</p>
          </div>
        </div>
      ) : (
        <label
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          className={`flex flex-col items-center justify-center gap-2 border-2 border-dashed rounded-lg p-6 cursor-pointer transition-colors ${
            dragOver ? 'border-[#0a3d8f] bg-blue-50' : 'border-gray-300 bg-gray-50 hover:border-[#0a3d8f] hover:bg-blue-50/50'
          } ${uploading ? 'opacity-60 pointer-events-none' : ''}`}
          data-testid={`${testid}-dropzone`}
        >
          <div className="w-11 h-11 rounded-full bg-blue-100 text-[#0a3d8f] flex items-center justify-center">
            <Upload size={20} />
          </div>
          <div className="text-center">
            <p className="text-sm font-semibold text-gray-900">
              {uploading ? 'Mengunggah...' : 'Klik untuk unggah gambar'}
            </p>
            <p className="text-xs text-gray-500 mt-0.5">atau seret &amp; lepas file di sini · tempel Ctrl+V · JPG/PNG/WEBP · maks 8MB</p>
          </div>
          <input
            ref={inputRef} type="file" accept="image/*" className="hidden"
            onChange={(e) => upload(e.target.files?.[0])}
            data-testid={`${testid}-file`}
          />
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); setShowUrlInput(true); }}
            className="mt-1 text-xs font-semibold text-[#0a3d8f] hover:underline flex items-center gap-1"
            data-testid={`${testid}-open-url`}
          >
            <LinkIcon size={12} /> atau tempel URL gambar
          </button>
        </label>
      )}

      {showUrlInput && (
        <div className="flex gap-2 items-stretch mt-1" data-testid={`${testid}-url-row`}>
          <input
            type="text"
            value={urlText}
            onChange={(e) => setUrlText(e.target.value)}
            placeholder="https://contoh.com/gambar.jpg"
            className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#0a3d8f]"
            data-testid={`${testid}-url-input`}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); applyUrl(); } }}
          />
          <button
            type="button"
            onClick={applyUrl}
            className="px-4 py-2 rounded-md bg-[#0a3d8f] hover:bg-[#082e6b] text-white text-sm font-semibold"
            data-testid={`${testid}-url-apply`}
          >
            Pakai URL
          </button>
        </div>
      )}

      {error && <p className="text-xs text-red-600 flex items-center gap-1"><X size={12} /> {error}</p>}
    </div>
  );
};

const Field = ({ f, value, onChange }) => {
  const shared = 'w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0a3d8f]/20 focus:border-[#0a3d8f]';
  if (f.type === 'richtext')
    return <RichTextField value={value ?? ''} onChange={onChange} testid={`field-${f.key}`} />;
  if (f.type === 'textarea')
    return <textarea rows={3} value={value ?? ''} onChange={(e) => onChange(e.target.value)} className={shared} data-testid={`field-${f.key}`} />;
  if (f.type === 'number')
    return <input type="number" value={value ?? ''} onChange={(e) => onChange(Number(e.target.value))} className={shared} data-testid={`field-${f.key}`} />;
  if (f.type === 'csv') {
    const csv = Array.isArray(value) ? value.join(', ') : (value ?? '');
    return <input type="text" value={csv} onChange={(e) => onChange(e.target.value.split(',').map(s => s.trim()).filter(Boolean))} className={shared} data-testid={`field-${f.key}`} />;
  }
  if (f.type === 'checkbox') {
    return (
      <label className="inline-flex items-center gap-2 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={!!value}
          onChange={(e) => onChange(e.target.checked)}
          className="w-4 h-4 accent-[#0a3d8f]"
          data-testid={`field-${f.key}`}
        />
        <span className="text-sm text-gray-700">Aktifkan</span>
      </label>
    );
  }
  if (f.type === 'select') {
    return (
      <select
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        className={shared}
        data-testid={`field-${f.key}`}
      >
        {(f.options || []).map((opt) => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
    );
  }
  if (f.type === 'image') {
    return <ImageField value={value} onChange={onChange} testid={`field-${f.key}`} />;
  }
  return <input type="text" value={value ?? ''} onChange={(e) => onChange(e.target.value)} className={shared} data-testid={`field-${f.key}`} />;
};

const EditModal = ({ open, onClose, schema, item, onSave }) => {
  const [form, setForm] = useState(item || {});
  useEffect(() => setForm(item || {}), [item]);
  if (!open) return null;

  const save = async () => {
    await onSave(form);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[110] bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="flex items-center justify-between border-b p-4">
          <h3 className="font-bold text-lg">{item?.id ? 'Edit' : 'Tambah'} {schema.label}</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-800" data-testid="edit-modal-close"><X size={20} /></button>
        </div>
        <div className="p-5 space-y-4">
          {schema.fields.map((f) => {
            if (f.type === 'section') {
              return (
                <div key={f.key} className="pt-4 pb-1 text-xs font-bold uppercase tracking-wider text-[#0a3d8f] border-b border-[#0a3d8f]/20">
                  {f.label}
                </div>
              );
            }
            return (
              <label key={f.key} className="block">
                <span className="text-xs font-semibold text-gray-700 uppercase tracking-wider">{f.label}</span>
                <div className="mt-1">
                  <Field f={f} value={form[f.key]} onChange={(v) => setForm((s) => ({ ...s, [f.key]: v }))} />
                </div>
              </label>
            );
          })}
        </div>
        <div className="flex items-center justify-end gap-2 border-t p-4 bg-gray-50">
          <button onClick={onClose} className="px-4 py-2 text-sm font-semibold text-gray-600 hover:text-gray-900">Batal</button>
          <button onClick={save} data-testid="edit-modal-save" className="inline-flex items-center gap-2 bg-[#0a3d8f] text-white px-5 py-2 rounded-md text-sm font-semibold hover:bg-[#082e6b]">
            <Save size={14} /> Simpan
          </button>
        </div>
      </div>
    </div>
  );
};

// ============ Generic collection manager ============
const CollectionManager = ({ collectionKey }) => {
  const schema = SCHEMAS[collectionKey];
  const { authAxios } = useAuth();
  const { refresh: refreshSite } = useSiteContent();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/content/${collectionKey}`);
      setItems(r.data || []);
    } finally {
      setLoading(false);
    }
  }, [collectionKey]);

  useEffect(() => { load(); }, [load]);

  const save = async (form) => {
    if (form.id) {
      await authAxios().put(`/content/${collectionKey}/${form.id}`, form);
    } else {
      await authAxios().post(`/content/${collectionKey}`, form);
    }
    await load();
    await refreshSite();
  };

  const del = async (id) => {
    if (!window.confirm('Hapus item ini?')) return;
    await authAxios().delete(`/content/${collectionKey}/${id}`);
    await load();
    await refreshSite();
  };

  const [scrapingId, setScrapingId] = useState(null);
  const [batchScraping, setBatchScraping] = useState(false);
  const scrapeProduct = async (id) => {
    if (!window.confirm('Scrape data lengkap dari jilongot.com untuk produk ini?\n\nProses ~30 detik (fetch + translate ke Bahasa Indonesia).')) return;
    setScrapingId(id);
    try {
      const r = await authAxios().post(`/admin/products/${id}/scrape`);
      const c = r.data.counts || {};
      alert(`Scrape selesai!\n\nGaleri: ${c.gallery}\nFitur: ${c.core_features}\nBlok fitur: ${c.feature_blocks}\nSpesifikasi: ${c.specifications}\nDownload: ${c.downloads}`);
      await load();
    } catch (e) {
      alert('Scrape gagal: ' + (e.response?.data?.detail || e.message));
    } finally {
      setScrapingId(null);
    }
  };

  const batchScrapeCategory = async (category) => {
    const count = items.filter(it => it.category === category).length;
    if (!window.confirm(`Scrape SEMUA ${count} produk kategori "${category}" dari jilongot.com?\n\nProses ~${count * 20} detik. Jangan tutup tab ini.`)) return;
    setBatchScraping(true);
    try {
      const r = await authAxios().post('/admin/products/scrape-batch', { category });
      const ok = r.data.results.filter(x => x.status === 'ok').length;
      const err = r.data.results.filter(x => x.status === 'error').length;
      const skip = r.data.results.filter(x => x.status === 'skipped').length;
      alert(`Batch scrape selesai!\n\nSukses: ${ok}\nGagal: ${err}\nDilewati: ${skip}`);
      await load();
    } catch (e) {
      alert('Batch scrape gagal: ' + (e.response?.data?.detail || e.message));
    } finally {
      setBatchScraping(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <h2 className="text-2xl font-extrabold text-gray-900">{schema.label}</h2>
        <div className="flex items-center gap-2">
          {collectionKey === 'products' && (
            <button
              onClick={() => batchScrapeCategory('fusion-splicing')}
              disabled={batchScraping}
              data-testid="batch-scrape-fusion"
              className="inline-flex items-center gap-2 bg-[#0a8f3d] text-white px-4 py-2 rounded-md text-sm font-semibold hover:bg-[#08702f] disabled:opacity-50"
              title="Scrape semua produk Fusion Splicer sekaligus"
            >
              <Download size={16} /> {batchScraping ? 'Sedang scrape...' : 'Batch Scrape Fusion'}
            </button>
          )}
          <button
            onClick={() => { setEditing({}); setOpen(true); }}
            data-testid={`add-${collectionKey}`}
            className="inline-flex items-center gap-2 bg-[#0a3d8f] text-white px-4 py-2 rounded-md text-sm font-semibold hover:bg-[#082e6b]"
          >
            <Plus size={16} /> Tambah
          </button>
        </div>
      </div>

      {loading ? (
        <p className="text-gray-500 text-sm">Memuat...</p>
      ) : items.length === 0 ? (
        <p className="text-gray-500 text-sm py-8 text-center bg-gray-50 rounded-lg">Belum ada data. Klik <b>Tambah</b> untuk membuat.</p>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left">
              <tr>
                <th className="px-4 py-3 font-semibold text-gray-700">#</th>
                <th className="px-4 py-3 font-semibold text-gray-700">Nama / Judul</th>
                <th className="px-4 py-3 font-semibold text-gray-700 hidden md:table-cell">Info</th>
                <th className="px-4 py-3 font-semibold text-gray-700 w-32">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, i) => (
                <tr key={it.id} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`row-${collectionKey}-${it.id}`}>
                  <td className="px-4 py-3 text-gray-500">{it.order ?? i + 1}</td>
                  <td className="px-4 py-3 font-semibold text-gray-900 line-clamp-2 max-w-md">
                    {it.name || it.title || it.label || it.badge || it.id}
                  </td>
                  <td className="px-4 py-3 text-gray-500 hidden md:table-cell truncate max-w-xs">
                    {it.category || it.nameId || it.tagline || it.website || it.link || '—'}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {collectionKey === 'products' && (
                        <button
                          onClick={() => scrapeProduct(it.id)}
                          disabled={scrapingId === it.id}
                          className="p-1.5 rounded hover:bg-green-50 text-[#0a8f3d] disabled:opacity-50"
                          aria-label="Scrape from Jilong"
                          title="Scrape data lengkap dari jilongot.com"
                          data-testid={`scrape-${it.id}`}
                        >
                          {scrapingId === it.id ? (
                            <span className="text-xs font-semibold">...</span>
                          ) : (
                            <Download size={14} />
                          )}
                        </button>
                      )}
                      <button
                        onClick={() => { setEditing(it); setOpen(true); }}
                        className="p-1.5 rounded hover:bg-blue-50 text-[#0a3d8f]"
                        aria-label="Edit"
                        data-testid={`edit-${it.id}`}
                      ><Edit3 size={14} /></button>
                      <button
                        onClick={() => del(it.id)}
                        className="p-1.5 rounded hover:bg-red-50 text-red-600"
                        aria-label="Delete"
                        data-testid={`delete-${it.id}`}
                      ><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <EditModal
        open={open} onClose={() => setOpen(false)}
        schema={schema} item={editing} onSave={save}
      />
    </div>
  );
};

// ============ Singleton editor ============
const SingletonEditor = ({ name }) => {
  const schema = SINGLETON_SCHEMAS[name];
  const { authAxios } = useAuth();
  const { refresh: refreshSite } = useSiteContent();
  const [form, setForm] = useState({});
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setForm({});
    axios.get(`${API}/singleton/${name}`)
      .then((r) => { if (!cancelled) setForm(r.data || {}); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [name]);

  const save = async () => {
    setError('');
    try {
      const r = await authAxios().put(`/singleton/${name}`, form);
      // Use response payload as source of truth
      if (r.data && typeof r.data === 'object') setForm(r.data);
      await refreshSite();
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Gagal menyimpan.');
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-5 sticky top-0 bg-gray-100 py-3 z-10 -mx-6 lg:-mx-10 px-6 lg:px-10">
        <h2 className="text-2xl font-extrabold text-gray-900">{schema.label}</h2>
        <button
          onClick={save} data-testid={`save-${name}`}
          className="inline-flex items-center gap-2 bg-[#0a3d8f] text-white px-5 py-2 rounded-md text-sm font-semibold hover:bg-[#082e6b] shadow-md"
        >
          <Save size={14} /> {saved ? 'Tersimpan!' : 'Simpan'}
        </button>
      </div>
      {loading ? <p className="text-gray-500 text-sm">Memuat...</p> : (
        <>
          <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-4">
            {error && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-3">{error}</p>}
            {schema.fields.map((f) => {
              if (f.type === 'section') {
                return (
                  <div key={`${name}-${f.key}`} className="pt-4 pb-1 text-xs font-bold uppercase tracking-wider text-[#0a3d8f] border-b border-[#0a3d8f]/20">
                    {f.label}
                  </div>
                );
              }
              return (
                <label key={`${name}-${f.key}`} className="block">
                  <span className="text-xs font-semibold text-gray-700 uppercase tracking-wider">{f.label}</span>
                  <div className="mt-1">
                    <Field f={f} value={form[f.key]} onChange={(v) => setForm((s) => ({ ...s, [f.key]: v }))} />
                  </div>
                </label>
              );
            })}
          </div>
          <div className="mt-6 flex justify-end">
            <button
              onClick={save} data-testid={`save-bottom-${name}`}
              className="inline-flex items-center gap-2 bg-[#0a3d8f] text-white px-6 py-2.5 rounded-md text-sm font-semibold hover:bg-[#082e6b] shadow-md"
            >
              <Save size={16} /> {saved ? 'Tersimpan!' : 'Simpan Perubahan'}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

// ============ Contact + Newsletter view ============
const WILAYAH_API = 'https://www.emsifa.com/api-wilayah-indonesia/api';

const ContactManager = () => {
  const { authAxios } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [subs, setSubs] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [cities, setCities] = useState([]);
  const [filterProvince, setFilterProvince] = useState('');
  const [filterCity, setFilterCity] = useState('');
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    axios.get(`${WILAYAH_API}/provinces.json`).then(r => setProvinces(r.data || [])).catch(() => {});
  }, []);

  const onFilterProvinceChange = async (val) => {
    setFilterProvince(val);
    setFilterCity('');
    setCities([]);
    if (!val) return;
    const prov = provinces.find(p => p.name === val);
    if (!prov) return;
    try {
      const r = await axios.get(`${WILAYAH_API}/regencies/${prov.id}.json`);
      setCities(r.data || []);
    } catch { setCities([]); }
  };

  const load = useCallback(async () => {
    const params = {};
    if (filterProvince) params.province = filterProvince;
    if (filterCity) params.city = filterCity;
    const [c, s] = await Promise.all([
      authAxios().get('/contact', { params }).then(r => r.data).catch(() => []),
      authAxios().get('/newsletter').then(r => r.data).catch(() => []),
    ]);
    setContacts(c); setSubs(s);
  }, [authAxios, filterProvince, filterCity]);
  useEffect(() => { load(); }, [load]);

  const delContact = async (id) => {
    if (!window.confirm('Hapus pesan ini?')) return;
    await authAxios().delete(`/contact/${id}`); load();
  };
  const delSub = async (id) => {
    if (!window.confirm('Hapus subscriber ini?')) return;
    await authAxios().delete(`/newsletter/${id}`); load();
  };

  const exportCsv = async () => {
    setDownloading(true);
    try {
      const params = {};
      if (filterProvince) params.province = filterProvince;
      if (filterCity) params.city = filterCity;
      const res = await authAxios().get('/contact/export.csv', { params, responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      a.download = `kontak-jilong-${stamp}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert('Gagal mengunduh CSV.');
    } finally {
      setDownloading(false);
    }
  };

  const resetFilter = () => { setFilterProvince(''); setFilterCity(''); setCities([]); };

  return (
    <div className="space-y-8">
      <div>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h2 className="text-2xl font-extrabold text-gray-900">Pesan Kontak ({contacts.length})</h2>
          <button
            onClick={exportCsv}
            disabled={downloading || contacts.length === 0}
            data-testid="contact-export-csv"
            className="inline-flex items-center gap-2 bg-[#0a8f3d] text-white px-4 py-2 rounded-md text-sm font-semibold hover:bg-[#08702f] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download size={14} /> {downloading ? 'Mengunduh...' : 'Export CSV'}
          </button>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4 mb-4 grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1">Filter Provinsi</label>
            <select
              value={filterProvince}
              onChange={(e) => onFilterProvinceChange(e.target.value)}
              data-testid="filter-province"
              className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:border-[#0a3d8f] bg-white"
            >
              <option value="">Semua Provinsi</option>
              {provinces.map(p => <option key={p.id} value={p.name}>{p.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1">Filter Kota/Kabupaten</label>
            <select
              value={filterCity}
              onChange={(e) => setFilterCity(e.target.value)}
              disabled={!filterProvince}
              data-testid="filter-city"
              className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:border-[#0a3d8f] bg-white disabled:bg-gray-50"
            >
              <option value="">Semua Kota/Kabupaten</option>
              {cities.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={resetFilter}
              disabled={!filterProvince && !filterCity}
              data-testid="filter-reset"
              className="w-full md:w-auto px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
            >
              Reset Filter
            </button>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          {contacts.length === 0 ? (
            <p className="text-gray-500 text-sm py-8 text-center">Belum ada pesan.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left">
                <tr>
                  <th className="px-4 py-3 font-semibold">Nama</th>
                  <th className="px-4 py-3 font-semibold">Email</th>
                  <th className="px-4 py-3 font-semibold hidden md:table-cell">Lokasi</th>
                  <th className="px-4 py-3 font-semibold hidden md:table-cell">Subjek</th>
                  <th className="px-4 py-3 font-semibold hidden lg:table-cell">Pesan</th>
                  <th className="px-4 py-3 font-semibold w-20"></th>
                </tr>
              </thead>
              <tbody>
                {contacts.map((c) => (
                  <tr key={c.id} className="border-t border-gray-100" data-testid={`contact-row-${c.id}`}>
                    <td className="px-4 py-3 font-semibold">{c.name}</td>
                    <td className="px-4 py-3 text-[#0a3d8f]">{c.email}</td>
                    <td className="px-4 py-3 hidden md:table-cell text-gray-600">
                      {[c.city, c.province].filter(Boolean).join(', ') || '—'}
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">{c.subject || '—'}</td>
                    <td className="px-4 py-3 hidden lg:table-cell text-gray-600 line-clamp-2 max-w-md">{c.message}</td>
                    <td className="px-4 py-3">
                      <button onClick={() => delContact(c.id)} className="p-1.5 rounded hover:bg-red-50 text-red-600"><Trash2 size={14} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div>
        <h2 className="text-2xl font-extrabold text-gray-900 mb-4">Newsletter Subscribers ({subs.length})</h2>
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          {subs.length === 0 ? (
            <p className="text-gray-500 text-sm py-8 text-center">Belum ada subscriber.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left">
                <tr>
                  <th className="px-4 py-3 font-semibold">Email</th>
                  <th className="px-4 py-3 font-semibold">Terdaftar</th>
                  <th className="px-4 py-3 w-20"></th>
                </tr>
              </thead>
              <tbody>
                {subs.map((s) => (
                  <tr key={s.id} className="border-t border-gray-100">
                    <td className="px-4 py-3 text-[#0a3d8f]">{s.email}</td>
                    <td className="px-4 py-3 text-gray-500">{new Date(s.created_at).toLocaleString()}</td>
                    <td className="px-4 py-3">
                      <button onClick={() => delSub(s.id)} className="p-1.5 rounded hover:bg-red-50 text-red-600"><Trash2 size={14} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

// ============ Dashboard ============
const CHART_COLORS = ['#0a3d8f', '#0a8f3d', '#f59e0b', '#a855f7', '#e11d48', '#0d9488', '#2563eb', '#65a30d', '#c026d3', '#f97316'];

const MONTHS_ID = ['Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni', 'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'];

// Build a list of month options: current month + previous 11 months
const buildMonthOptions = () => {
  const opts = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = `${MONTHS_ID[d.getMonth()]} ${d.getFullYear()}`;
    opts.push({ value, label });
  }
  return opts;
};

const ExportPDFButton = () => {
  const { authAxios } = useAuth();
  const options = buildMonthOptions();
  const [month, setMonth] = useState(options[0].value);
  const [loading, setLoading] = useState(false);

  const download = async () => {
    setLoading(true);
    try {
      const res = await authAxios().get(`/admin/leads-analytics/export.pdf?month=${month}`, {
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `laporan-leads-${month}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert('Gagal mengunduh laporan. Coba lagi.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center gap-2" data-testid="export-pdf-controls">
      <select
        value={month}
        onChange={(e) => setMonth(e.target.value)}
        data-testid="export-pdf-month"
        className="text-sm border border-gray-200 rounded-md px-2.5 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-[#0a3d8f]/20"
      >
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      <button
        onClick={download}
        disabled={loading}
        data-testid="export-pdf-btn"
        className="inline-flex items-center gap-2 bg-[#0a3d8f] hover:bg-[#082e6b] disabled:opacity-60 text-white text-sm font-semibold px-3 py-1.5 rounded-md transition-colors"
      >
        <Download size={14} /> {loading ? 'Menyiapkan…' : 'Unduh PDF'}
      </button>
    </div>
  );
};

const Dashboard = () => {
  const { authAxios } = useAuth();
  const { config } = useSiteContent();
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [chartType, setChartType] = useState('bar');
  const [analytics, setAnalytics] = useState(null);
  const [gaConfig, setGaConfig] = useState(null);

  useEffect(() => {
    authAxios().get('/admin/dashboard').then(r => setStats(r.data)).catch(() => setStats({}));
    authAxios().get('/admin/leads-by-province').then(r => setLeads(r.data || [])).catch(() => setLeads([]));
    authAxios().get('/admin/leads-analytics?days=30').then(r => setAnalytics(r.data)).catch(() => setAnalytics(null));
    axios.get(`${API}/seo/config`).then(r => setGaConfig(r.data)).catch(() => setGaConfig({}));
  }, [authAxios]);

  const cards = [
    { label: 'Pesan Kontak', value: stats?.contacts ?? '—', Icon: Mail, color: 'bg-blue-50 text-[#0a3d8f]' },
    { label: 'Newsletter', value: stats?.subscribers ?? '—', Icon: MessageSquare, color: 'bg-green-50 text-[#0a8f3d]' },
    { label: 'Hero Slides', value: stats?.hero_slides ?? '—', Icon: ImageIcon, color: 'bg-amber-50 text-amber-700' },
    { label: 'Produk', value: stats?.products ?? '—', Icon: Package, color: 'bg-purple-50 text-purple-700' },
    { label: 'Berita', value: stats?.news_items ?? '—', Icon: Newspaper, color: 'bg-rose-50 text-rose-700' },
    { label: 'Mitra', value: stats?.partners ?? '—', Icon: Users2, color: 'bg-teal-50 text-teal-700' },
  ];

  const totalLeads = leads.reduce((s, l) => s + l.count, 0);
  const gaId = gaConfig?.ga4_measurement_id?.trim() || '';
  const gaActive = !!gaId;

  const formatDate = (iso) => {
    if (!iso) return '';
    try {
      const d = typeof iso === 'string' ? new Date(iso) : iso;
      return d.toLocaleString('id-ID', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return String(iso); }
  };
  const shortDate = (ymd) => {
    if (!ymd) return '';
    const [, m, d] = ymd.split('-');
    return `${d}/${m}`;
  };

  return (
    <div>
      <h2 className="text-2xl font-extrabold text-gray-900 mb-6">Dashboard</h2>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
        {cards.map((c) => (
          <div key={c.label} className="bg-white rounded-xl border border-gray-200 p-5" data-testid={`dashboard-${c.label}`}>
            <div className={`w-11 h-11 rounded-lg ${c.color} flex items-center justify-center mb-3`}>
              <c.Icon size={20} />
            </div>
            <div className="text-3xl font-extrabold text-gray-900">{c.value}</div>
            <div className="text-xs text-gray-500 uppercase tracking-wider mt-1">{c.label}</div>
          </div>
        ))}
      </div>

      {/* ================= Google Analytics Card ================= */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6" data-testid="google-analytics-card">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-lg bg-orange-50 text-orange-600 flex items-center justify-center">
              <Activity size={20} />
            </div>
            <div>
              <h3 className="text-lg font-extrabold text-gray-900">Google Analytics</h3>
              <p className="text-xs text-gray-500 mt-0.5">Pantau lalu lintas & perilaku pengunjung di GA4</p>
            </div>
          </div>
          <span
            className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${gaActive ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}
            data-testid="ga-status-badge"
          >
            <span className={`w-2 h-2 rounded-full ${gaActive ? 'bg-green-500' : 'bg-red-500'}`} />
            {gaActive ? 'Terhubung' : 'Belum diatur'}
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
          <div className="rounded-lg border border-gray-100 bg-gray-50 p-3">
            <div className="text-xs uppercase tracking-wider text-gray-500 mb-1">Measurement ID</div>
            <div className="font-mono text-gray-900 break-all" data-testid="ga-measurement-id">{gaId || <span className="text-gray-400 italic">— belum diisi —</span>}</div>
          </div>
          <div className="rounded-lg border border-gray-100 bg-gray-50 p-3">
            <div className="text-xs uppercase tracking-wider text-gray-500 mb-1">Property</div>
            <div className="text-gray-900">{gaActive ? gaConfig?.site_name || 'JILONG Indonesia' : <span className="text-gray-400 italic">— tidak aktif —</span>}</div>
          </div>
          <div className="rounded-lg border border-gray-100 bg-gray-50 p-3">
            <div className="text-xs uppercase tracking-wider text-gray-500 mb-1">Event Tracking</div>
            <div className="text-gray-900">{gaActive ? 'page_view · WA click · form submit' : '—'}</div>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <a
            href="https://analytics.google.com/analytics/web/"
            target="_blank"
            rel="noreferrer"
            data-testid="ga-open-console"
            className="inline-flex items-center gap-2 bg-[#0a3d8f] hover:bg-[#082e6b] text-white text-sm font-semibold px-4 py-2 rounded-md transition-colors"
          >
            <ExternalLink size={14} /> Buka GA4 Console
          </a>
          <a
            href="https://analytics.google.com/analytics/web/#/p0/reports/intelligenthome"
            target="_blank"
            rel="noreferrer"
            data-testid="ga-open-realtime"
            className="inline-flex items-center gap-2 bg-white border border-gray-200 hover:bg-gray-50 text-gray-800 text-sm font-semibold px-4 py-2 rounded-md transition-colors"
          >
            <Activity size={14} /> Laporan Realtime
          </a>
          {!gaActive && (
            <span className="inline-flex items-center gap-1.5 text-xs text-gray-500 self-center">
              Atur Measurement ID di tab <b className="text-gray-700 mx-1">SEO Umum</b> untuk mengaktifkan
            </span>
          )}
        </div>
      </div>

      {/* ================= Leads Analytics Header + KPI ================= */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3 mt-2">
        <div className="flex items-center gap-2">
          <BarChart3 size={20} className="text-[#0a3d8f]" />
          <h3 className="text-lg font-extrabold text-gray-900">Leads Analytics</h3>
        </div>
        <ExportPDFButton />
      </div>

      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4" data-testid="leads-kpi-total">
          <div className="text-xs uppercase tracking-wider text-gray-500">Total Leads</div>
          <div className="text-2xl md:text-3xl font-extrabold text-gray-900 mt-1">{analytics?.total ?? '—'}</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4" data-testid="leads-kpi-7d">
          <div className="text-xs uppercase tracking-wider text-gray-500">7 Hari Terakhir</div>
          <div className="text-2xl md:text-3xl font-extrabold text-[#0a3d8f] mt-1">{analytics?.last_7_days ?? '—'}</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4" data-testid="leads-kpi-30d">
          <div className="text-xs uppercase tracking-wider text-gray-500">30 Hari Terakhir</div>
          <div className="text-2xl md:text-3xl font-extrabold text-[#0a8f3d] mt-1">{analytics?.last_30_days ?? '—'}</div>
        </div>
      </div>

      {/* Trend Chart */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6" data-testid="leads-trend-card">
        <div className="flex items-center gap-2 mb-3">
          <TrendingUp size={16} className="text-[#0a3d8f]" />
          <h4 className="text-base font-bold text-gray-900">Tren Harian (30 hari)</h4>
        </div>
        {(!analytics || analytics.trend?.length === 0) ? (
          <p className="text-sm text-gray-500 py-8 text-center">Belum ada data tren.</p>
        ) : (
          <div style={{ width: '100%', height: 240 }}>
            <ResponsiveContainer>
              <AreaChart data={analytics.trend} margin={{ top: 5, right: 15, left: 0, bottom: 5 }}>
                <defs>
                  <linearGradient id="leadFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#0a3d8f" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#0a3d8f" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                <XAxis dataKey="date" tickFormatter={shortDate} tick={{ fontSize: 10 }} interval={Math.max(0, Math.floor((analytics.trend?.length || 1) / 10))} />
                <YAxis allowDecimals={false} tick={{ fontSize: 10 }} width={30} />
                <Tooltip formatter={(v) => [`${v} lead`, 'Jumlah']} labelFormatter={(l) => `Tanggal: ${l}`} />
                <Area type="monotone" dataKey="count" stroke="#0a3d8f" strokeWidth={2} fill="url(#leadFill)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Province chart (existing behavior preserved) */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6" data-testid="leads-by-province-card">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-2">
            <MapPin size={16} className="text-[#0a3d8f]" />
            <div>
              <h4 className="text-base font-bold text-gray-900">Sebaran Leads per Provinsi</h4>
              <p className="text-xs text-gray-500 mt-0.5">Total {totalLeads} lead dari {leads.length} wilayah</p>
            </div>
          </div>
          <div className="inline-flex rounded-md border border-gray-200 overflow-hidden text-xs font-semibold">
            <button
              onClick={() => setChartType('bar')} data-testid="chart-bar-toggle"
              className={`px-3 py-1.5 ${chartType === 'bar' ? 'bg-[#0a3d8f] text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
            >Bar</button>
            <button
              onClick={() => setChartType('pie')} data-testid="chart-pie-toggle"
              className={`px-3 py-1.5 ${chartType === 'pie' ? 'bg-[#0a3d8f] text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
            >Pie</button>
          </div>
        </div>

        {leads.length === 0 ? (
          <p className="text-sm text-gray-500 py-10 text-center">Belum ada data leads dengan provinsi terisi.</p>
        ) : (
          <div style={{ width: '100%', height: 360 }}>
            <ResponsiveContainer>
              {chartType === 'bar' ? (
                <BarChart data={leads} margin={{ top: 10, right: 20, left: 0, bottom: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                  <XAxis dataKey="province" tick={{ fontSize: 11 }} angle={-25} textAnchor="end" interval={0} height={70} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v) => [`${v} lead`, 'Jumlah']} />
                  <Bar dataKey="count" fill="#0a3d8f" radius={[6, 6, 0, 0]} />
                </BarChart>
              ) : (
                <PieChart>
                  <Tooltip formatter={(v) => [`${v} lead`, 'Jumlah']} />
                  <Legend verticalAlign="bottom" wrapperStyle={{ fontSize: 12 }} />
                  <Pie
                    data={leads}
                    dataKey="count"
                    nameKey="province"
                    cx="50%"
                    cy="45%"
                    outerRadius={110}
                    label={(e) => `${e.province} (${e.count})`}
                    labelLine={false}
                  >
                    {leads.map((_, i) => (
                      <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                </PieChart>
              )}
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* By Subject & By City side-by-side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <div className="bg-white rounded-xl border border-gray-200 p-5" data-testid="leads-by-subject-card">
          <h4 className="text-base font-bold text-gray-900 mb-3">Leads per Subjek</h4>
          {(!analytics || analytics.by_subject?.length === 0) ? (
            <p className="text-sm text-gray-500 py-8 text-center">Belum ada data.</p>
          ) : (
            <div style={{ width: '100%', height: 280 }}>
              <ResponsiveContainer>
                <BarChart data={analytics.by_subject} layout="vertical" margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                  <XAxis type="number" allowDecimals={false} tick={{ fontSize: 10 }} />
                  <YAxis type="category" dataKey="subject" tick={{ fontSize: 11 }} width={130} />
                  <Tooltip formatter={(v) => [`${v} lead`, 'Jumlah']} />
                  <Bar dataKey="count" fill="#0a8f3d" radius={[0, 6, 6, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-5" data-testid="leads-by-city-card">
          <h4 className="text-base font-bold text-gray-900 mb-3">Top 15 Kota / Kabupaten</h4>
          {(!analytics || analytics.by_city?.length === 0) ? (
            <p className="text-sm text-gray-500 py-8 text-center">Belum ada data.</p>
          ) : (
            <div style={{ width: '100%', height: 280 }}>
              <ResponsiveContainer>
                <BarChart data={analytics.by_city} layout="vertical" margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                  <XAxis type="number" allowDecimals={false} tick={{ fontSize: 10 }} />
                  <YAxis type="category" dataKey="city" tick={{ fontSize: 11 }} width={130} />
                  <Tooltip formatter={(v) => [`${v} lead`, 'Jumlah']} />
                  <Bar dataKey="count" fill="#f59e0b" radius={[0, 6, 6, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      {/* Recent Leads Table */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 mb-8" data-testid="leads-recent-card">
        <h4 className="text-base font-bold text-gray-900 mb-3">10 Lead Terbaru</h4>
        {(!analytics || analytics.recent?.length === 0) ? (
          <p className="text-sm text-gray-500 py-8 text-center">Belum ada lead masuk.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b border-gray-200 text-xs uppercase tracking-wider text-gray-500">
                  <th className="py-2 pr-3 font-semibold">Tanggal</th>
                  <th className="py-2 pr-3 font-semibold">Nama</th>
                  <th className="py-2 pr-3 font-semibold">Email</th>
                  <th className="py-2 pr-3 font-semibold">WhatsApp</th>
                  <th className="py-2 pr-3 font-semibold">Kota</th>
                  <th className="py-2 pr-3 font-semibold">Provinsi</th>
                  <th className="py-2 pr-3 font-semibold">Subjek</th>
                </tr>
              </thead>
              <tbody>
                {analytics.recent.map((r) => (
                  <tr key={r.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-2 pr-3 whitespace-nowrap text-gray-600">{formatDate(r.created_at)}</td>
                    <td className="py-2 pr-3 font-medium text-gray-900">{r.name || '—'}</td>
                    <td className="py-2 pr-3 text-gray-700">{r.email || '—'}</td>
                    <td className="py-2 pr-3 text-gray-700">{r.phone || '—'}</td>
                    <td className="py-2 pr-3 text-gray-700">{r.city || '—'}</td>
                    <td className="py-2 pr-3 text-gray-700">{r.province || '—'}</td>
                    <td className="py-2 pr-3 text-gray-700">{r.subject || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// ============ Main Admin Panel ============

// Small reusable password input with show/hide eye toggle.
const PasswordInput = ({ value, onChange, placeholder, testid, required, minLength }) => {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <input
        type={show ? 'text' : 'password'}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        minLength={minLength}
        data-testid={testid}
        className="w-full border border-gray-200 rounded-md px-3 py-2 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-[#0a3d8f]/20"
      />
      <button
        type="button"
        onClick={() => setShow((v) => !v)}
        aria-label={show ? 'Sembunyikan' : 'Tampilkan'}
        tabIndex={-1}
        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-gray-400 hover:text-gray-700"
        data-testid={testid ? `${testid}-toggle` : undefined}
      >
        {show ? <EyeOff size={16} /> : <Eye size={16} />}
      </button>
    </div>
  );
};

// ----- Change Password (self) -----
const ChangePasswordCard = () => {
  const { authAxios } = useAuth();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState(null); // { type, text }

  const submit = async (e) => {
    e.preventDefault();
    setMsg(null);
    if (next.length < 6) return setMsg({ type: 'err', text: 'Password baru minimal 6 karakter.' });
    if (next !== confirm) return setMsg({ type: 'err', text: 'Konfirmasi password tidak cocok.' });
    setLoading(true);
    try {
      await authAxios().post('/auth/change-password', { current_password: current, new_password: next });
      setMsg({ type: 'ok', text: 'Password berhasil diubah.' });
      setCurrent(''); setNext(''); setConfirm('');
    } catch (err) {
      const d = err.response?.data?.detail;
      setMsg({ type: 'err', text: typeof d === 'string' ? d : 'Gagal mengubah password.' });
    } finally { setLoading(false); }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-6 max-w-lg" data-testid="change-password-card">
      <div className="flex items-center gap-2 mb-4">
        <KeyRound size={18} className="text-[#0a3d8f]" />
        <h3 className="font-bold text-lg">Ubah Password Sendiri</h3>
      </div>
      <form onSubmit={submit} className="space-y-3">
        <PasswordInput value={current} onChange={(e) => setCurrent(e.target.value)} placeholder="Password saat ini" required testid="cp-current" />
        <PasswordInput value={next} onChange={(e) => setNext(e.target.value)} placeholder="Password baru (min. 6 karakter)" required minLength={6} testid="cp-new" />
        <PasswordInput value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Konfirmasi password baru" required testid="cp-confirm" />
        {msg && (
          <div className={`text-xs px-3 py-2 rounded-md border ${msg.type === 'ok' ? 'bg-green-50 text-green-700 border-green-200' : 'bg-red-50 text-red-700 border-red-200'}`} data-testid="cp-msg">
            {msg.text}
          </div>
        )}
        <button type="submit" disabled={loading} data-testid="cp-submit" className="inline-flex items-center gap-2 bg-[#0a3d8f] text-white text-sm font-semibold px-4 py-2 rounded-md hover:bg-[#082e6b] disabled:opacity-60">
          <Save size={14} /> {loading ? 'Menyimpan…' : 'Simpan Password Baru'}
        </button>
      </form>
    </div>
  );
};

// ----- Manage other users (super admin only) -----
const UserManagerCard = () => {
  const { user: me, authAxios } = useAuth();
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ username: '', password: '', role: 'admin' });
  const [pwEdits, setPwEdits] = useState({}); // { userId: password }
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await authAxios().get('/admin/users');
      setUsers(r.data || []);
    } catch { setUsers([]); }
  }, [authAxios]);

  useEffect(() => { load(); }, [load]);

  const flash = (type, text) => { setMsg({ type, text }); setTimeout(() => setMsg(null), 3500); };

  const createUser = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await authAxios().post('/admin/users', form);
      setForm({ username: '', password: '', role: 'admin' });
      await load();
      flash('ok', 'Admin baru berhasil dibuat.');
    } catch (err) {
      const d = err.response?.data?.detail;
      flash('err', typeof d === 'string' ? d : 'Gagal membuat admin.');
    } finally { setLoading(false); }
  };

  const changeRole = async (u, role) => {
    try {
      await authAxios().put(`/admin/users/${u.id}`, { role });
      await load();
      flash('ok', `Role @${u.username} diubah ke ${role}.`);
    } catch (err) {
      const d = err.response?.data?.detail;
      flash('err', typeof d === 'string' ? d : 'Gagal mengubah role.');
    }
  };

  const resetPassword = async (u) => {
    const pw = (pwEdits[u.id] || '').trim();
    if (pw.length < 6) return flash('err', 'Password minimal 6 karakter.');
    try {
      await authAxios().put(`/admin/users/${u.id}`, { new_password: pw });
      setPwEdits({ ...pwEdits, [u.id]: '' });
      flash('ok', `Password @${u.username} berhasil direset.`);
    } catch (err) {
      const d = err.response?.data?.detail;
      flash('err', typeof d === 'string' ? d : 'Gagal mereset password.');
    }
  };

  const removeUser = async (u) => {
    if (!window.confirm(`Hapus admin @${u.username}?`)) return;
    try {
      await authAxios().delete(`/admin/users/${u.id}`);
      await load();
      flash('ok', `@${u.username} dihapus.`);
    } catch (err) {
      const d = err.response?.data?.detail;
      flash('err', typeof d === 'string' ? d : 'Gagal menghapus user.');
    }
  };

  return (
    <div className="space-y-6" data-testid="user-manager">
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex items-center gap-2 mb-4">
          <ShieldCheck size={18} className="text-[#0a3d8f]" />
          <h3 className="font-bold text-lg">Buat Admin Baru</h3>
        </div>
        <form onSubmit={createUser} className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <input required minLength={3} maxLength={64} placeholder="Username" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} data-testid="um-username" className="border border-gray-200 rounded-md px-3 py-2 text-sm" />
          <input required minLength={6} type="password" placeholder="Password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="um-password" className="border border-gray-200 rounded-md px-3 py-2 text-sm" />
          <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="um-role" className="border border-gray-200 rounded-md px-3 py-2 text-sm">
            <option value="admin">Admin</option>
            <option value="superadmin">Super Admin</option>
          </select>
          <button type="submit" disabled={loading} data-testid="um-submit" className="inline-flex items-center justify-center gap-2 bg-[#0a3d8f] text-white text-sm font-semibold px-4 py-2 rounded-md hover:bg-[#082e6b] disabled:opacity-60">
            <Plus size={14} /> Tambah
          </button>
        </form>
        {msg && (
          <div className={`mt-3 text-xs px-3 py-2 rounded-md border ${msg.type === 'ok' ? 'bg-green-50 text-green-700 border-green-200' : 'bg-red-50 text-red-700 border-red-200'}`} data-testid="um-msg">
            {msg.text}
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b flex items-center gap-2">
          <Users2 size={18} className="text-[#0a3d8f]" />
          <h3 className="font-bold text-lg">Daftar Admin</h3>
          <span className="text-xs text-gray-500 ml-2">{users.length} akun</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600 text-xs uppercase">
              <tr>
                <th className="text-left px-6 py-3">Username</th>
                <th className="text-left px-6 py-3">Role</th>
                <th className="text-left px-6 py-3">Dibuat</th>
                <th className="text-left px-6 py-3 w-[320px]">Reset Password</th>
                <th className="text-right px-6 py-3">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const isSelf = u.id === me?.id;
                return (
                  <tr key={u.id} className="border-t border-gray-100" data-testid={`um-row-${u.username}`}>
                    <td className="px-6 py-3 font-medium">
                      @{u.username}{isSelf && <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 font-semibold">Anda</span>}
                    </td>
                    <td className="px-6 py-3">
                      <select
                        value={u.role}
                        disabled={isSelf}
                        onChange={(e) => changeRole(u, e.target.value)}
                        data-testid={`um-role-${u.username}`}
                        className="border border-gray-200 rounded-md px-2 py-1 text-xs disabled:bg-gray-50 disabled:text-gray-500"
                      >
                        <option value="admin">Admin</option>
                        <option value="superadmin">Super Admin</option>
                      </select>
                    </td>
                    <td className="px-6 py-3 text-xs text-gray-500">{(u.created_at || '').slice(0, 10)}</td>
                    <td className="px-6 py-3">
                      <div className="flex items-center gap-2">
                        <input
                          type="password"
                          placeholder="Password baru (min. 6)"
                          value={pwEdits[u.id] || ''}
                          onChange={(e) => setPwEdits({ ...pwEdits, [u.id]: e.target.value })}
                          data-testid={`um-pw-${u.username}`}
                          className="border border-gray-200 rounded-md px-2 py-1 text-xs w-full"
                        />
                        <button
                          type="button"
                          onClick={() => resetPassword(u)}
                          data-testid={`um-reset-${u.username}`}
                          className="inline-flex items-center gap-1 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs px-3 py-1.5 rounded-md whitespace-nowrap"
                        >
                          <KeyRound size={12} /> Reset
                        </button>
                      </div>
                    </td>
                    <td className="px-6 py-3 text-right">
                      <button
                        type="button"
                        disabled={isSelf}
                        onClick={() => removeUser(u)}
                        data-testid={`um-delete-${u.username}`}
                        className="inline-flex items-center gap-1 text-red-600 hover:text-red-700 hover:bg-red-50 text-xs px-3 py-1.5 rounded-md disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <Trash2 size={12} /> Hapus
                      </button>
                    </td>
                  </tr>
                );
              })}
              {users.length === 0 && (
                <tr><td colSpan={5} className="px-6 py-8 text-center text-gray-400 text-xs">Belum ada admin lain.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const UsersPage = () => {
  const { user } = useAuth();
  const isSuper = user?.role === 'superadmin';
  return (
    <div className="space-y-6" data-testid="users-page">
      <div>
        <h2 className="text-2xl font-extrabold text-gray-900">Akun & Password</h2>
        <p className="text-sm text-gray-500">Kelola password Anda sendiri{isSuper ? ' dan admin lain' : ''}.</p>
      </div>
      <ChangePasswordCard />
      {isSuper && <UserManagerCard />}
    </div>
  );
};

// ============ Main Admin Panel ============
const TABS = [
  { key: 'dashboard', label: 'Dashboard', Icon: LayoutDashboard },
  { key: 'hero-slides', label: 'Hero Slides', Icon: ImageIcon },
  { key: 'product-categories', label: 'Beranda', Icon: LayoutGrid },
  { key: 'product-subcategories', label: 'Sub-Kategori', Icon: LayoutGrid },
  { key: 'about-content', label: 'Sejarah', Icon: History },
  { key: 'stats', label: 'Statistik', Icon: History },
  { key: 'products', label: 'Produk', Icon: Package },
  { key: 'news-items', label: 'Berita', Icon: Newspaper },
  { key: 'support-items', label: 'Dukungan', Icon: LifeBuoy },
  { key: 'support-manuals', label: 'Manual Produk', Icon: LifeBuoy },
  { key: 'support-videos', label: 'Video Tutorial', Icon: LifeBuoy },
  { key: 'contact', label: 'Kontak', Icon: MessageSquare },
  { key: 'partners', label: 'Mitra', Icon: Users2 },
  { key: 'seo-config', label: 'SEO Umum', Icon: Settings },
  { key: 'seo-pages', label: 'SEO Per-Halaman', Icon: Settings },
  { key: 'site-config', label: 'Konfigurasi', Icon: Settings },
  { key: 'users', label: 'Akun & Password', Icon: KeyRound },
];

const AdminPanel = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('dashboard');

  const doLogout = () => { logout(); navigate('/admin/login', { replace: true }); };

  const render = () => {
    if (tab === 'dashboard') return <Dashboard />;
    if (tab === 'contact') return <ContactManager />;
    if (tab === 'users') return <UsersPage />;
    if (SINGLETON_SCHEMAS[tab]) return <SingletonEditor key={tab} name={tab} />;
    if (SCHEMAS[tab]) return <CollectionManager key={tab} collectionKey={tab} />;
    return null;
  };

  return (
    <div className="min-h-screen bg-gray-100 flex" data-testid="admin-panel">
      <aside className="w-64 bg-[#0a1e3c] text-gray-300 flex flex-col shrink-0 min-h-screen sticky top-0">
        <div className="px-5 py-5 border-b border-white/10">
          <div className="text-white text-lg font-extrabold">JILONG Admin</div>
          <div className="text-xs text-gray-400 mt-0.5">@{user?.username}</div>
        </div>
        <nav className="flex-1 py-3 space-y-1 overflow-y-auto">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`tab-${t.key}`}
              className={`w-full flex items-center gap-3 px-5 py-2.5 text-sm text-left transition-colors ${
                tab === t.key ? 'bg-[#0a3d8f] text-white font-semibold' : 'hover:bg-white/5 hover:text-white'
              }`}
            >
              <t.Icon size={16} /> {t.label}
            </button>
          ))}
        </nav>
        <div className="border-t border-white/10 p-4">
          <button
            onClick={doLogout}
            data-testid="admin-logout"
            className="w-full inline-flex items-center gap-2 justify-center bg-white/5 hover:bg-red-600 hover:text-white text-sm text-gray-300 py-2 rounded-md transition-colors"
          >
            <LogOut size={14} /> Logout
          </button>
          <a href="/" className="block text-center text-xs text-gray-500 mt-3 hover:text-white">← Kembali ke Website</a>
        </div>
      </aside>

      <main className="flex-1 min-w-0 p-6 lg:p-10 overflow-x-hidden">
        {render()}
      </main>
    </div>
  );
};

export default AdminPanel;
